import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, DatePicker, Input, Select, Space, Typography, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetToPlnP6ProgressUpdatesBetweenDates } from '../../../hooks/genericHooks';
import type { ToPlnP6ProgressUpdateRow } from '../../../types';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

const tokenizeAnd = (q: string): string[] => {
  const s = String(q || '').trim().toLowerCase();
  if (!s) return [];
  return s.split(/\s+/).map(t => t.trim()).filter(Boolean);
};

const getDefaultRange = (): [Dayjs, Dayjs] => {
  const end = dayjs();
  const start = end.subtract(30, 'day');
  return [start, end];
};

const toOpt = (v: any) => ({ value: String(v ?? '').trim(), label: String(v ?? '').trim() });

const ToPlnActualsReportList: React.FC = () => {
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(() => getDefaultRange());

  const [search, setSearch] = useState('');

  const [wpFilter, setWpFilter] = useState<string>('');
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');

  const effectiveRange = range ?? getDefaultRange();

  const startIso = useMemo(() => effectiveRange[0].startOf('day').format('YYYY-MM-DD'), [effectiveRange]);
  const endIso = useMemo(() => effectiveRange[1].endOf('day').format('YYYY-MM-DD'), [effectiveRange]);

  const { data: listResp, isLoading, error } = useGetToPlnP6ProgressUpdatesBetweenDates(startIso, endIso);

  const rows: ToPlnP6ProgressUpdateRow[] = useMemo(() => {
    if (!listResp) return [];
    if (Array.isArray(listResp)) return listResp as any;
    if (Array.isArray((listResp as any)?.data)) return (listResp as any).data as any;
    if (Array.isArray((listResp as any)?.rows)) return (listResp as any).rows as any;
    if (Array.isArray((listResp as any)?.recordset)) return (listResp as any).recordset as any;
    return [];
  }, [listResp]);

  useEffect(() => {
    if (error) {
      message.error((error as any)?.message || 'Failed to load actuals report rows');
    }
  }, [error]);

  const searchTokens = useMemo(() => tokenizeAnd(search), [search]);

  const matchesSearchScope = useMemo(() => {
    return (r: ToPlnP6ProgressUpdateRow) => {
      if (!searchTokens.length) return true;
      const hay = [
        (r as any)?.update_date,
        (r as any)?.activity_name,
        (r as any)?.rsrc_short_name,
        (r as any)?.rsrc_unit_name,
        (r as any)?.provider_code,
        (r as any)?.reported_qty,
        (r as any)?.remarks,
        (r as any)?.created_by,
        (r as any)?.created_at,
      ]
        .map((v) => (v == null ? '' : String(v)))
        .join(' ')
        .toLowerCase();

      return searchTokens.every((t) => hay.includes(t));
    };
  }, [searchTokens]);

  const applyFilters = useMemo(() => {
    return (r: ToPlnP6ProgressUpdateRow, opts: { wp?: boolean; activity?: boolean; provider?: boolean }) => {
      const wpOn = opts.wp !== false;
      const actOn = opts.activity !== false;
      const provOn = opts.provider !== false;

      if (wpOn && wpFilter && String((r as any)?.wp_ref_num ?? '').trim() !== wpFilter) return false;
      if (actOn && activityFilter && String((r as any)?.activity_code ?? '').trim() !== activityFilter) return false;
      if (provOn && providerFilter && String((r as any)?.provider_name ?? '').trim() !== providerFilter) return false;

      return matchesSearchScope(r);
    };
  }, [activityFilter, matchesSearchScope, providerFilter, wpFilter]);

  const wpOptions = useMemo(() => {
    const set = new Map<string, string>();
    (rows || []).filter((r) => applyFilters(r, { wp: false, activity: true, provider: true })).forEach((r) => {
      const v = String((r as any)?.wp_ref_num ?? '').trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (!set.has(k)) set.set(k, v);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(toOpt);
  }, [applyFilters, rows]);

  const handleResetFilters = useCallback(() => {
    setWpFilter('');
    setActivityFilter('');
    setProviderFilter('');
    setSearch('');
    setRange(getDefaultRange());
  }, []);

  const activityOptions = useMemo(() => {
    const set = new Map<string, string>();
    (rows || []).filter((r) => applyFilters(r, { wp: true, activity: false, provider: true })).forEach((r) => {
      const v = String((r as any)?.activity_code ?? '').trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (!set.has(k)) set.set(k, v);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(toOpt);
  }, [applyFilters, rows]);

  const providerOptions = useMemo(() => {
    const set = new Map<string, string>();
    (rows || []).filter((r) => applyFilters(r, { wp: true, activity: true, provider: false })).forEach((r) => {
      const v = String((r as any)?.provider_name ?? '').trim();
      if (!v) return;
      const k = v.toLowerCase();
      if (!set.has(k)) set.set(k, v);
    });
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b)).map(toOpt);
  }, [applyFilters, rows]);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((r) => {
      if (wpFilter && String(r?.wp_ref_num ?? '').trim() !== wpFilter) return false;
      if (activityFilter && String(r?.activity_code ?? '').trim() !== activityFilter) return false;
      if (providerFilter && String(r?.provider_name ?? '').trim() !== providerFilter) return false;

      return matchesSearchScope(r);
    });
  }, [activityFilter, matchesSearchScope, providerFilter, rows, wpFilter]);

  const columns = useMemo(() => {
    return [
      { title: 'Date', dataIndex: 'update_date', key: 'update_date', width: 110 },
      { title: 'WP', dataIndex: 'wp_ref_num', key: 'wp_ref_num', width: 110 },
      { title: 'Activity', dataIndex: 'activity_code', key: 'activity_code', width: 120 },
      { title: 'Activity Name', dataIndex: 'activity_name', key: 'activity_name', width: 240 },
      { title: 'Resource', dataIndex: 'rsrc_short_name', key: 'rsrc_short_name', width: 140 },
      { title: 'Unit', dataIndex: 'rsrc_unit_name', key: 'rsrc_unit_name', width: 70 },
      { title: 'Provider Code', dataIndex: 'provider_code', key: 'provider_code', width: 110 },
      { title: 'Provider Name', dataIndex: 'provider_name', key: 'provider_name', width: 220 },
      { title: 'Qty', dataIndex: 'reported_qty', key: 'reported_qty', width: 80, align: 'right' as const },
      { title: 'Remarks', dataIndex: 'remarks', key: 'remarks', width: 240 },
      { title: 'By', dataIndex: 'created_by', key: 'created_by', width: 110 },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (v: any) => {
          const d = dayjs(v);
          return d.isValid() ? d.format('YYYY-MM-DD HH:mm') : (v == null ? '' : String(v));
        },
        exportValue: (r: any) => r?.created_at,
      },
    ];
  }, []);

  return (
    <Card style={{ margin: 16, padding: 0 }} styles={{ body: { padding: 0 } }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '16px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Title level={4} style={{ margin: 0 }}>Actuals Report - Listings</Title>
        <Button onClick={handleResetFilters} disabled={isLoading}>
          Reset Filters
        </Button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '8px 24px 8px 24px', borderBottom: '1px solid #f0f0f0' }}>
        <Space wrap size={12}>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => {
              if (!v || !v[0] || !v[1]) {
                setRange(null);
                return;
              }
              setRange([v[0], v[1]]);
            }}
            style={{ width: 220 }}
            disabled={isLoading}
            allowClear
            disabledDate={(d) => (d ? d.isAfter(dayjs(), 'day') : false)}
          />
          <Select
            allowClear
            showSearch
            placeholder="WP No"
            value={wpFilter || undefined}
            onChange={(v) => setWpFilter(v || '')}
            options={wpOptions}
            style={{ width: 150 }}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(String(input ?? '').toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Activity No"
            value={activityFilter || undefined}
            onChange={(v) => setActivityFilter(v || '')}
            options={activityOptions}
            style={{ width: 180 }}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(String(input ?? '').toLowerCase())}
          />
          <Select
            allowClear
            showSearch
            placeholder="Provider Name"
            value={providerFilter || undefined}
            onChange={(v) => setProviderFilter(v || '')}
            options={providerOptions}
            style={{ width: 220 }}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(String(input ?? '').toLowerCase())}
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search (AND) - excludes WP/Activity/Provider"
            allowClear
            style={{ width: 260 }}
          />
        </Space>
      </div>

      <div style={{ padding: '8px 24px 16px 24px' }}>
        <StandardTable
          columns={columns as any}
          dataSource={filteredRows}
          rowKey={(r: any) => String(r?.id ?? `${r?.update_date ?? ''}-${r?.activity_code ?? ''}-${r?.rsrc_short_name ?? ''}-${r?.provider_code ?? ''}`)}
          loading={isLoading}
          enableExport={true}
          exportFileName="actuals_report_list"
          scroll={{ x: 'max-content' }}
        />
      </div>
    </Card>
  );
};

export default ToPlnActualsReportList;
